import {
  Briefcase,
  Ear,
  Flag,
  Heart,
  House,
  Leaf,
  MessageCircleMore,
  Sparkle,
  Sparkles,
  UsersRound,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from "react";
import type { UserProfile } from "./types";
import logoUrl from "./assets/pearlie-logo.png";

/* The same warm palette, pearl and rose logo as the iPhone app
   (Pearlie/Theme/PearliePalette.swift and Pearlie/Components/PearlieAura.swift).
   Change one, change both. */

export type Hue = "peach" | "rose" | "blush" | "lilac" | "butter" | "berry" | "sage" | "sky" | "mint";

export const HUES: Record<Hue, string> = {
  peach: "#FFB59A",
  rose: "#F2849E",
  blush: "#F7A6C4",
  lilac: "#B79CF2",
  butter: "#FFD49A",
  berry: "#D45A80",
  sage: "#9FD3B5",
  sky: "#9EC5F7",
  mint: "#A8E0C8",
};

export const BASE_HUES: Hue[] = ["peach", "rose", "lilac", "butter"];

type Option = { icon: LucideIcon; hue: Hue; reaction: string };

const OPTIONS: Record<string, Option> = {
  "Relationships": { icon: Heart, hue: "rose", reaction: "Love stuff. We'll get into it." },
  "Friendships": { icon: UsersRound, hue: "blush", reaction: "Good friends change everything." },
  "Family": { icon: House, hue: "butter", reaction: "Family is complicated. I get it." },
  "Work or studies": { icon: Briefcase, hue: "sky", reaction: "Work and school take a lot. Noted." },
  "Confidence": { icon: Sparkle, hue: "peach", reaction: "Confidence. Let's talk about it." },
  "Something else": { icon: MessageCircleMore, hue: "mint", reaction: "Anything goes. Bring it." },
  "Just exploring": { icon: Sparkles, hue: "lilac", reaction: "No pressure. Look around first." },
};

export function optionFor(name: string): Option {
  return OPTIONS[name] ?? { icon: Sparkle, hue: "blush", reaction: "Got it." };
}

/* The four support styles offered since 1.1.1, the same as the app
   (PearlieSupportStyle in Pearlie/Theme/PearliePalette.swift). Each summary
   describes what the server's matching instruction asks of Pearlie
   (SUPPORT_STYLES in backend/src/pearlie.ts). Older saved tones still work. */
export const SUPPORT_STYLES = [
  "Honest and kind",
  "Gentle encouragement",
  "Help me take action",
  "Just listen for now",
];

export const DEFAULT_SUPPORT_STYLE = "Honest and kind";

export const TONE_ICONS: Record<string, LucideIcon> = {
  "Honest and kind": Heart,
  "Gentle encouragement": Leaf,
  "Help me take action": Flag,
  "Just listen for now": Ear,
};

export const TONE_PROMPT = "he hasn't texted back in two days. should i text him?";

/* A real reply from the live server (2026-10-01, default tone). The example
   stays put and each support style is described in plain words rather than
   shown with invented answers. */
export const TONE_REPLY = "If your last text asked something or you had plans, send one follow-up. If it was just a casual message, I’d leave it. One follow-up is enough; after that, let his silence give you the information.";

export function toneSummary(tone: string): string {
  switch (tone) {
    case "Gentle encouragement": return "Softer, with small steps.";
    case "Help me take action": return "Ends with one clear next step.";
    case "Just listen for now": return "Listens first. Advice only if you ask.";
    default: return "The truth, said with care.";
  }
}

function styleHue(style: string): Hue {
  switch (style) {
    case "Gentle encouragement": return "mint";
    case "Help me take action": return "butter";
    case "Just listen for now": return "lilac";
    default: return "rose";
  }
}

/** The line under "Pearlie is here." on the arrival screen. */
export function arrivalLine(style: string): string {
  switch (style) {
    case "Gentle encouragement": return "Go easy on yourself. She will too.";
    case "Help me take action": return "Bring the problem. Leave with a next step.";
    case "Just listen for now": return "Say it however it comes. She’s listening.";
    default: return "Say it straight. She’ll be honest and kind.";
  }
}

/** Pearlie's pearl in this person's colours, the same rule as the app: two
    warm base tones plus the colours of the topic and support style picked. */
export function auraHues(topic: string | null, style: string): Hue[] {
  const result: Hue[] = ["peach", "rose"];
  const picked = [...(topic ? [optionFor(topic).hue] : []), styleHue(style)];
  for (const hue of picked) if (!result.includes(hue)) result.push(hue);
  return result;
}

/* Keyed by goal. Since 1.1.1 a new profile's only goal is the topic picked for
   "What's on your mind today?"; the older goal names stay for older profiles. */
const GOAL_STARTERS: Record<string, string> = {
  "Relationships": "I need advice about my love life.",
  "Family": "My family is really getting to me.",
  "Work or studies": "Work or school is stressing me out.",
  "Love life": "I'm getting mixed signals and I don't know what to do.",
  "Confidence": "I keep second-guessing myself.",
  "Friendships": "A friend has been distant lately and it hurts.",
  "Career": "I want to ask for more money at work.",
  "Money": "I'm stressed about money and I keep it to myself.",
  "Habits": "I keep starting things and giving up.",
  "My body": "I've been really hard on myself about my body.",
  "Peace of mind": "My head won't slow down at night.",
  "Saying no": "I say yes to everyone and I'm worn out.",
};

const SITUATION_STARTERS: Record<string, string> = {
  "Relationship": "Something happened in my relationship and I can't stop thinking about it.",
  "Family": "My family is really getting to me.",
  "Work": "Work is draining me.",
  "Studying": "School is stressing me out.",
  "Money": "I'm stressed about money and I keep it to myself.",
  "Health": "I'm worried about my health.",
  "Building something": "I'm building something and I keep doubting myself.",
  "Honestly, nothing steady": "I feel kind of lost right now.",
};

const GENERAL_STARTERS = [
  "Something's been on my mind and I can't shake it.",
  "I keep putting off something important.",
  "I need to make a decision and I'm stuck.",
  "I think I messed up.",
];

/** Three first lines drawn from her own onboarding answers. */
export function startersFor(profile: UserProfile): string[] {
  const starters: string[] = [];
  const add = (line?: string) => {
    if (line && !starters.includes(line) && starters.length < 3) starters.push(line);
  };
  profile.goals.forEach(goal => add(GOAL_STARTERS[goal]));
  profile.currentSituations.forEach(situation => add(SITUATION_STARTERS[situation]));
  GENERAL_STARTERS.forEach(add);
  return starters;
}

/* ---------- The pearl ---------- */

function rgba(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

const ease = (x: number) => {
  const c = Math.min(Math.max(x, 0), 1);
  return c * c * (3 - 2 * c);
};

type DrawOptions = {
  hues: Hue[];
  weights: number[];
  diameter: number;
  glow: boolean;
  sparkles: boolean;
  t: number;
  bloom: number;
  dark: boolean;
};

function drawAura(ctx: CanvasRenderingContext2D, side: number, o: DrawOptions) {
  const c = side / 2;
  const radius = (o.diameter / 2) * (1 + 0.055 * o.bloom);
  ctx.clearRect(0, 0, side, side);

  if (o.glow) {
    const outer = radius * 2.25;
    const g = ctx.createRadialGradient(c, c, 0, c, c, outer);
    g.addColorStop(0.25, rgba(HUES.rose, (o.dark ? 0.34 : 0.26) + 0.18 * o.bloom));
    g.addColorStop(0.55, rgba(HUES.peach, o.dark ? 0.12 : 0.13));
    g.addColorStop(1, rgba(HUES.peach, 0));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, side, side);
  }

  ctx.save();
  ctx.beginPath();
  ctx.arc(c, c, radius, 0, Math.PI * 2);
  ctx.clip();

  const body = ctx.createRadialGradient(c - radius * 0.3, c - radius * 0.35, radius * 0.05, c - radius * 0.3, c - radius * 0.35, radius * 1.25);
  body.addColorStop(0, "#FFF4EC");
  body.addColorStop(0.55, "#FFD3C9");
  body.addColorStop(1, "#F4A5BF");
  ctx.fillStyle = body;
  ctx.fillRect(c - radius, c - radius, radius * 2, radius * 2);

  o.hues.forEach((hue, index) => {
    const w = o.weights[index] ?? 1;
    if (w <= 0.001) return;
    const a = o.t * 0.42 * (0.75 + 0.17 * index) + index * 1.9;
    const x = c + Math.cos(a) * radius * 0.48;
    const y = c + Math.sin(a * 1.23 + index) * radius * 0.44;
    const r = radius * (0.68 + 0.12 * Math.sin(a * 1.4 + index)) * w;
    const g = ctx.createRadialGradient(x, y, 0, x, y, Math.max(r, 0.1));
    g.addColorStop(0, rgba(HUES[hue], 0.9));
    g.addColorStop(0.55, rgba(HUES[hue], 0.42));
    g.addColorStop(1, rgba(HUES[hue], 0));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  });

  const shade = ctx.createRadialGradient(c - radius * 0.25, c - radius * 0.3, 0, c - radius * 0.25, c - radius * 0.3, radius * 1.05);
  shade.addColorStop(0.52, "rgba(0,0,0,0)");
  shade.addColorStop(1, o.dark ? "rgba(61,10,41,0.45)" : "rgba(168,66,102,0.24)");
  ctx.fillStyle = shade;
  ctx.fillRect(c - radius, c - radius, radius * 2, radius * 2);

  ctx.save();
  ctx.translate(c - radius * 0.32, c - radius * 0.46);
  ctx.scale(1, 0.62);
  const gloss = ctx.createRadialGradient(0, 0, 0, 0, 0, radius * 0.55);
  gloss.addColorStop(0, "rgba(255,255,255,0.85)");
  gloss.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = gloss;
  ctx.beginPath();
  ctx.arc(0, 0, radius * 0.55, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  ctx.restore();

  const rimWidth = Math.max(0.75, radius * 0.018);
  const rim = ctx.createLinearGradient(c - radius, c - radius, c + radius, c + radius);
  rim.addColorStop(0, "rgba(255,255,255,0.9)");
  rim.addColorStop(0.5, "rgba(255,255,255,0.14)");
  rim.addColorStop(1, "rgba(255,255,255,0.5)");
  ctx.strokeStyle = rim;
  ctx.lineWidth = rimWidth;
  ctx.beginPath();
  ctx.arc(c, c, radius - rimWidth / 2, 0, Math.PI * 2);
  ctx.stroke();

  if (o.sparkles) {
    for (let index = 0; index < 7; index += 1) {
      const angle = index * 0.9 + 0.3 + o.t * 0.06 * (index % 2 === 0 ? -1 : 1);
      const orbit = radius * (1.32 + (index % 3) * 0.2 + 0.08 * o.bloom);
      const x = c + Math.cos(angle) * orbit;
      const y = c + Math.sin(angle) * orbit * 0.92;
      const twinkle = 0.5 + 0.5 * Math.sin(o.t * 2.1 + index * 2.3);
      const s = radius * (0.028 + (index % 3) * 0.012) * (0.55 + twinkle * 0.6);
      ctx.fillStyle = o.dark
        ? `rgba(255,237,242,${0.3 + 0.6 * twinkle})`
        : `rgba(201,71,112,${0.2 + 0.5 * twinkle})`;
      ctx.beginPath();
      ctx.moveTo(x, y - s * 2);
      ctx.quadraticCurveTo(x, y, x + s * 2, y);
      ctx.quadraticCurveTo(x, y, x, y + s * 2);
      ctx.quadraticCurveTo(x, y, x - s * 2, y);
      ctx.quadraticCurveTo(x, y, x, y - s * 2);
      ctx.fill();
    }
  }
}

function prefersReducedMotion() {
  return typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function isDark() {
  const root = document.documentElement;
  return root.dataset.mode === "dark" || (root.dataset.mode === "system" && root.dataset.systemDark === "true");
}

/** Pearlie's pearl. Colours that join it grow in; a change of `pulse` blooms it. */
export function PearlieAura({
  hues = BASE_HUES,
  diameter,
  glow = true,
  sparkles = true,
  animated = true,
  energy = 1,
  pulse = 0,
  className = "",
  children,
}: {
  hues?: Hue[];
  diameter: number;
  glow?: boolean;
  sparkles?: boolean;
  animated?: boolean;
  energy?: number;
  pulse?: number;
  className?: string;
  children?: React.ReactNode;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const state = useRef({
    arrivals: new Map<Hue, number>(),
    departures: new Map<Hue, number>(),
    hues: hues,
    pulseAt: -1e9,
    energy,
    clock: 0,
    last: 0,
  });
  const side = glow || sparkles ? diameter * 2.3 : diameter;

  useEffect(() => {
    const s = state.current;
    const now = performance.now();
    if (s.arrivals.size === 0) hues.forEach(hue => s.arrivals.set(hue, -1e9));
    hues.forEach(hue => {
      if (!s.hues.includes(hue)) { s.arrivals.set(hue, now); s.departures.delete(hue); }
    });
    s.hues.forEach(hue => { if (!hues.includes(hue)) s.departures.set(hue, now); });
    s.hues = hues;
  }, [hues]);

  useEffect(() => { state.current.pulseAt = performance.now(); }, [pulse]);
  useEffect(() => { state.current.energy = energy; }, [energy]);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const scale = Math.min(window.devicePixelRatio || 1, 3);
    canvas.width = Math.round(side * scale);
    canvas.height = Math.round(side * scale);
    const still = !animated || prefersReducedMotion();
    let frame = 0;

    const render = (now: number) => {
      const s = state.current;
      const dt = s.last ? Math.min(0.1, (now - s.last) / 1000) : 0;
      s.last = now;
      s.clock += dt * s.energy;
      const t = still ? 1.7 : s.clock;
      const elapsed = (now - s.pulseAt) / 1000;
      const bloom = !still && elapsed >= 0 && elapsed < 0.75 ? Math.sin(Math.PI * elapsed / 0.75) : 0;
      const drawn: Hue[] = [...s.hues];
      s.departures.forEach((left, hue) => {
        if (!drawn.includes(hue) && (now - left) / 1000 < 0.9) drawn.push(hue);
      });
      const weights = drawn.map(hue => {
        let weight = 1;
        const arrived = s.arrivals.get(hue);
        if (arrived !== undefined && arrived > -1e8 && !still) weight *= ease((now - arrived) / 900);
        const left = s.departures.get(hue);
        if (left !== undefined && !s.hues.includes(hue)) weight *= 1 - ease((now - left) / 900);
        return weight;
      });
      ctx.setTransform(scale, 0, 0, scale, 0, 0);
      drawAura(ctx, side, { hues: drawn, weights, diameter, glow, sparkles, t, bloom, dark: isDark() });
      if (!still) frame = requestAnimationFrame(render);
    };
    frame = requestAnimationFrame(render);
    return () => cancelAnimationFrame(frame);
  }, [animated, diameter, glow, side, sparkles, hues.join(",")]);

  return (
    <div className={`pearlie-aura ${className}`} style={{ width: diameter, height: diameter }} aria-hidden="true">
      <canvas ref={ref} style={{ width: side, height: side }} />
      {children}
    </div>
  );
}

/* ---------- The script S ---------- */

/** Pearlie's logo: a rose with a pearl at its heart, on black, as a circle.
    The same art as the app icon and `PearlieLogo` on iPhone (marketing/brand).
    `reveal` 0…1 blooms it in over `duration` ms; a change of `pulse` pops it. */
export function PearlieLogo({
  size,
  reveal = 1,
  duration = 0,
  pulse = 0,
  className = "",
}: {
  size: number;
  reveal?: number;
  duration?: number;
  pulse?: number;
  className?: string;
}) {
  const [popping, setPopping] = useState(false);
  useEffect(() => {
    if (!pulse) return;
    setPopping(true);
    const id = window.setTimeout(() => setPopping(false), 160);
    return () => window.clearTimeout(id);
  }, [pulse]);
  const scale = (0.82 + 0.18 * reveal) * (popping ? 1.1 : 1);
  return (
    <img
      className={`pearlie-logo ${className}`}
      src={logoUrl}
      alt=""
      aria-hidden="true"
      width={size}
      height={size}
      style={{
        width: size,
        height: size,
        opacity: reveal,
        transform: `rotate(${(1 - reveal) * -14}deg) scale(${scale})`,
        transition: duration
          ? `transform ${duration}ms cubic-bezier(.55,.1,.25,1), opacity ${duration}ms ease`
          : "transform 180ms cubic-bezier(.3,1.4,.5,1)",
      }}
    />
  );
}

/** Pearlie's face in the conversation: her logo, still. */
export function PearlieAvatar({ size, className = "" }: { size: number; className?: string }) {
  return <PearlieLogo size={size} className={`pearlie-avatar ${className}`} />;
}

/* ---------- Small pieces ---------- */

/** A hand-drawn underline that writes itself as the name gets longer. */
export function PearlieUnderline({ progress }: { progress: number }) {
  return (
    <svg className="pearlie-underline" viewBox="0 0 300 12" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id="pearlie-sunset" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor={HUES.peach} />
          <stop offset="0.5" stopColor={HUES.rose} />
          <stop offset="1" stopColor={HUES.lilac} />
        </linearGradient>
      </defs>
      <line x1="0" y1="6" x2="300" y2="6" className="track" />
      <path
        d="M0 6 C18 2.6 42 2.6 60 6 C78 9.4 102 9.4 120 6 C138 2.6 162 2.6 180 6 C198 9.4 222 9.4 240 6 C258 2.6 282 2.6 300 6"
        pathLength={1}
        fill="none"
        stroke="url(#pearlie-sunset)"
        strokeWidth="3.5"
        strokeLinecap="round"
        style={{ strokeDasharray: "1 2", strokeDashoffset: 1 - progress, opacity: progress > 0 ? 1 : 0, transition: "stroke-dashoffset .5s cubic-bezier(.2,.8,.2,1)" }}
      />
    </svg>
  );
}

/** A ruler you slide to your age. It starts on a faint guess and only counts
    once you move it, because the under-18 safety rules depend on the answer. */
export function AgeDial({
  value,
  onChange,
  min = 13,
  max = 100,
  start = 25,
}: {
  value: number | null;
  onChange: (age: number) => void;
  min?: number;
  max?: number;
  start?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Only a real touch, drag, wheel or key counts. Scroll snapping settles the
  // ruler by itself after it first appears, and that must never set an age.
  const touched = useRef(false);
  const drag = useRef<{ x: number; left: number; moved: boolean } | null>(null);
  const spacing = 18;
  const ages = useMemo(() => Array.from({ length: max - min + 1 }, (_, i) => min + i), [min, max]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollLeft = ((value ?? start) - min) * spacing;
  }, []);

  const touch = () => { touched.current = true; };

  const settle = () => {
    const el = ref.current;
    if (!el || !touched.current) return;
    const age = Math.min(max, Math.max(min, min + Math.round(el.scrollLeft / spacing)));
    if (age !== value) {
      onChange(age);
      navigator.vibrate?.(4);
    }
  };

  const scrollTo = (age: number) => {
    ref.current?.scrollTo({ left: (age - min) * spacing, behavior: "smooth" });
    onChange(age);
  };

  const keyboard = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    touch();
    const current = value ?? start;
    if (event.key === "ArrowRight" || event.key === "ArrowUp") { event.preventDefault(); scrollTo(Math.min(max, current + 1)); }
    if (event.key === "ArrowLeft" || event.key === "ArrowDown") { event.preventDefault(); scrollTo(Math.max(min, current - 1)); }
  };

  // Mouse drag for desktop. The pointer is only captured once it has moved,
  // so a plain click still reaches the tick under it.
  const down = (event: ReactPointerEvent<HTMLDivElement>) => {
    touch();
    if (event.pointerType !== "mouse" || !ref.current) return;
    drag.current = { x: event.clientX, left: ref.current.scrollLeft, moved: false };
  };
  const move = (event: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || !ref.current) return;
    if (!d.moved && Math.abs(event.clientX - d.x) > 4) {
      d.moved = true;
      ref.current.setPointerCapture(event.pointerId);
    }
    if (d.moved) ref.current.scrollLeft = d.left - (event.clientX - d.x);
  };
  const up = () => {
    const d = drag.current;
    drag.current = null;
    if (!d?.moved || !ref.current) return;
    const el = ref.current;
    el.scrollTo({ left: Math.round(el.scrollLeft / spacing) * spacing, behavior: "smooth" });
  };

  return (
    <div
      className="age-dial"
      role="slider"
      tabIndex={0}
      aria-label="Your age"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value ?? undefined}
      aria-valuetext={value ? `${value} years old` : "Not set"}
      onKeyDown={keyboard}
    >
      <div
        className="age-dial-track"
        ref={ref}
        onScroll={settle}
        onWheel={event => {
          touch();
          if (Math.abs(event.deltaY) > Math.abs(event.deltaX) && ref.current) ref.current.scrollLeft += event.deltaY;
        }}
        onTouchStart={touch}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
      >
        {ages.map(age => (
          <button key={age} type="button" tabIndex={-1} className={`age-tick ${age % 5 === 0 ? "major" : ""}`} onClick={() => { touch(); scrollTo(age); }}>
            <i />
            <span>{age % 5 === 0 ? age : ""}</span>
          </button>
        ))}
      </div>
      <b className="age-dial-mark" />
    </div>
  );
}
